import { FilterDefinition } from '@app/core/list-view/types';
import { auditReports } from '@mi-front/api-clients';
import { parseAsString } from 'nuqs';
import { defineMessage } from 'react-intl';
import { GenericSelectFilter } from '../../../../../../core/list-view/base-filters/GenericSelectFilter';
import { GenericTextFilter } from '../../../../../../core/list-view/base-filters/GenericTextFilter';
import { reportNameMessages } from '../../../reports/constants';
import { aodRunStatusMessages, triggerMessages } from '../../messages';
import { AodRulesFilter } from './AodRulesFilter';

export type { FilterDefinition };

/**
 * Status options for the status filter (matches getStatusLabel keys in AodRunStatus).
 */
export const statusOptions = [
  {
    value: 'COMPLETED_WITH_EXPORT',
    message: aodRunStatusMessages.COMPLETED_WITH_EXPORT,
  },
  {
    value: 'COMPLETED_WITHOUT_EXPORT',
    message: aodRunStatusMessages.COMPLETED_WITHOUT_EXPORT,
  },
  {
    value: 'IN_PROGRESS',
    message: aodRunStatusMessages.IN_PROGRESS,
  },
  {
    value: 'FAILED',
    message: aodRunStatusMessages.FAILED,
  },
] as const;

/**
 * Report type options for rule.reportType filter.
 */
export const reportTypeOptions = [
  {
    value: auditReports.AodRuleReportTypeEnum.AUDIT_GIP_STATUS,
    message: reportNameMessages.AUDIT_GIP_STATUS,
  },
  {
    value: auditReports.AodRuleReportTypeEnum.AUDIT_GIP_ALTERNATIVE_REPORT,
    message: reportNameMessages.AUDIT_GIP_ALTERNATIVE_REPORT,
  },
] as const;

/**
 * Type (trigger) options for the type filter.
 */
export const triggerOptions = [
  {
    value: auditReports.AodRunTypeEnum.EOW,
    message: triggerMessages.EOW,
  },
  {
    value: auditReports.AodRunTypeEnum.ORDER_END,
    message: triggerMessages.ORDER_END,
  },
  {
    value: auditReports.AodRunTypeEnum.MANUAL,
    message: triggerMessages.MANUAL,
  },
] as const;

/**
 * Configuration array for all AOD Runs List V2 filters.
 * Permanent: rule.name, status. Addable: rule.reportType, type, order.
 * Uses default Props generic so both GenericTextFilter and GenericSelectFilter (which require extra props like options) type-check.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- heterogeneous array: some components need extra props (e.g. GenericSelectFilter.options)
export const AOD_RUNS_FILTERS_CONFIG: FilterDefinition<any, string>[] = [
  {
    name: 'rule.id',
    label: defineMessage({
      id: 'audit.aod.runs.rule',
      defaultMessage: 'Regla',
    }),
    parser: parseAsString,
    isPermanent: true,
    component: AodRulesFilter,
  },
  {
    name: 'status',
    label: defineMessage({
      id: 'audit.aod.runs.status',
      defaultMessage: 'Estado',
    }),
    parser: parseAsString.withDefault(''),
    isPermanent: true,
    component: GenericSelectFilter,
    componentProps: {
      options: statusOptions,
      sx: {
        minWidth: '20ch',
      },
    },
  },
  {
    name: 'order||$contL',
    label: defineMessage({
      id: 'audit.aod.runs.order',
      defaultMessage: 'Orden',
    }),
    parser: parseAsString.withDefault(''),
    isPermanent: true,
    component: GenericTextFilter,
  },
  {
    name: 'rule.reportType',
    label: defineMessage({
      id: 'audit.aod.runs.reportType',
      defaultMessage: 'Tipo de reporte',
    }),
    parser: parseAsString,
    isPermanent: false,
    component: GenericSelectFilter,
    componentProps: {
      options: reportTypeOptions,
      sx: {
        minWidth: '20ch',
      },
    },
  },
  {
    name: 'type',
    label: defineMessage({
      id: 'audit.aod.runs.type',
      defaultMessage: 'Trigger',
    }),
    parser: parseAsString,
    isPermanent: false,
    component: GenericSelectFilter,
    componentProps: {
      options: triggerOptions,
    },
  },
];
